/**
 * Registry de painéis para o agendador de exportação.
 *
 * Cada página (Acabamento, Marcenaria, etc.) publica seu "snapshot" no
 * registry assim que `resumo`, `secoes` e `visual` ficam prontos via
 * `useEffect`. O agendador consome o snapshot mais recente.
 *
 * Como o snapshot só existe quando a página foi visitada, o agendador
 * tem 3 estratégias quando dispara para um painel:
 *
 *  1. **Snapshot fresco** (< STALENESS_MS): usa direto.
 *  2. **Snapshot stale**: ainda usa, mas marca no histórico como "snapshot
 *     antigo — dados podem estar defasados".
 *  3. **Sem snapshot**: registra "pulado — abra a página {nome}".
 *
 * Não persiste em localStorage por design — snapshots são em memória, vivem
 * apenas pela sessão. Recarregar a aba zera o registry.
 */

const snapshots = new Map();

const STALENESS_MS = 10 * 60 * 1000;

/**
 * Inicializa um Map global em `window.__pharuerpSnapshots`. Isso permite
 * que o agendador na aba PRINCIPAL leia os snapshots gerados dentro de
 * IFRAMES ocultos (cada iframe tem sua própria instância deste módulo
 * e seu próprio `snapshots` Map — só o `window` é cross-context).
 */
function obterMapGlobal() {
  if (typeof window === "undefined") return null;
  if (!window.__pharuerpSnapshots) {
    window.__pharuerpSnapshots = new Map();
  }
  return window.__pharuerpSnapshots;
}

/**
 * @param {string} painelId
 * @param {Object} payload { titulo, resumo, secoes, visual? }
 * @param {Object} [opts]
 * @param {() => HTMLElement|null} [opts.getVisualRoot]  ref da página, para PNG/JPG
 */
export function registrarSnapshot(painelId, payload, opts = {}) {
  if (!painelId) return;
  const entry = {
    payload,
    ts: Date.now(),
    getVisualRoot: opts.getVisualRoot || null,
  };
  snapshots.set(painelId, entry);
  const mapGlobal = obterMapGlobal();
  if (mapGlobal) mapGlobal.set(painelId, entry);
}

/**
 * Zera todos os snapshots. Chamado no logout: o Map de módulo sobrevive à
 * troca de usuário, e um agendamento do usuário B encontrava o snapshot
 * "fresco" do usuário A e exportava os números dele — sem nem abrir o iframe.
 * Mesmo motivo pelo qual o cache de imagens autenticadas já era limpo.
 */
export function limparSnapshots() {
  snapshots.clear();
  const mapGlobal = obterMapGlobal();
  if (mapGlobal) mapGlobal.clear();
}

export function removerSnapshot(painelId) {
  if (!painelId) return;
  snapshots.delete(painelId);
  const mapGlobal = obterMapGlobal();
  if (mapGlobal) mapGlobal.delete(painelId);
}

export function obterSnapshot(painelId) {
  return snapshots.get(painelId) || null;
}

export function snapshotEstaFresco(snapshot, agora = Date.now()) {
  if (!snapshot) return false;
  return agora - snapshot.ts < STALENESS_MS;
}

export function listarPaineisComSnapshot() {
  return Array.from(snapshots.keys());
}

/**
 * Lista de painéis exportáveis exposta na UI do agendador. Os IDs devem
 * bater com os IDs registrados via `registrarSnapshot` pelas páginas.
 */
export const PAINEIS_DISPONIVEIS = [
  // Fluxo operacional (ordem do funil)
  // /clientes renderiza a lista v2, que NÃO publica snapshot; quem publica
  // é a planilha legada. Apontar para /clientes fazia o iframe do agendador
  // esperar 60s por um snapshot que aquela rota nunca produz.
  { id: "clientes",              label: "Clientes (legado)",         rota: "/legado/clientes" },
  { id: "liberacao",             label: "Liberação",                 rota: "/liberacao" },
  { id: "definicao-acabamentos", label: "Definição de Acabamentos",  rota: "/acabamentos" },
  { id: "medicao",               label: "Medição",                   rota: "/medicao" },
  { id: "executivo",             label: "Projeto Executivo",         rota: "/executivo" },
  { id: "revisao-executivo",     label: "Revisão do Executivo",      rota: "/revisao" },
  { id: "aprovacao-executivo",   label: "Aprovação do Executivo",    rota: "/aprovacao" },
  { id: "ppcp",                  label: "PPCP — Liberação",          rota: "/ppcp" },
  { id: "compras",               label: "Compras",                   rota: "/compras" },
  { id: "ferragens",             label: "Ferragens",                 rota: "/ferragens" },
  { id: "industrializacao",      label: "Industrialização (Máquinas)", rota: "/industrializacao" },
  { id: "marcenaria",            label: "Marcenaria",                rota: "/marcenaria" },
  { id: "terceiros",             label: "Terceiros",                 rota: "/terceiros" },
  { id: "acabamento",            label: "Pintura & Lacagem",         rota: "/acabamento" },
  { id: "planejamento-entregas", label: "Planejamento de Entregas",  rota: "/entregas" },
  // KPIs
  { id: "marceneiros-lista",     label: "Marceneiros — lista",       rota: "/marceneiros" },
  // "Marceneiros — detalhe" saiu do catálogo: o snapshot só existe em
  // /marceneiros/:id e o modelo de agendamento (paineis: [{painelId}]) não
  // carrega o id do marceneiro. Oferecê-lo era prometer o que não dá para
  // cumprir — 60s de timeout por formato, todo dia.
  { id: "relatorios-legado",     label: "Relatórios — ambientes em aberto", rota: "/legado/relatorios" },
  { id: "capacidade-fabrica",    label: "Capacidade da Fábrica",     rota: "/legado/capacidade" },
  { id: "area-pcp-historico",    label: "Área do PCP — histórico de execuções", rota: "/legado/area-pcp/historico" },
  { id: "kpis-ppcp",             label: "KPIs PPCP · TOC",           rota: "/kpis-ppcp" },
  { id: "kpis-tempos",           label: "KPIs de Tempo",             rota: "/kpis-tempos" },
  { id: "visao-financeira",      label: "Visão Financeira",          rota: "/financeiro" },
];

export function obterRota(painelId) {
  return PAINEIS_DISPONIVEIS.find((p) => p.id === painelId)?.rota || null;
}

export function obterLabel(painelId) {
  return PAINEIS_DISPONIVEIS.find((p) => p.id === painelId)?.label || painelId;
}
